/// <amd-module name="VSS/Platform/Content" />
import { Observable } from "VSS/Core/Observable";
import { IVssObservableService, IVssPageContext, ServiceOptions, Services, VssObservableService } from "./Context";

export type ContentLoaderHTMLElement = HTMLLinkElement | HTMLScriptElement;

declare global {
    interface Window {
        __contentLoaded: (contentElement: HTMLElement, succeeded: boolean, loadEnd?: number, previousResourceLoadEnd?: number) => IContentAttributes;
        __loadScript: (content: IContentAttributes) => Promise<IContentAttributes>;
        __moduleNamespaces: string[];
        __useAadToken: boolean;
    }
}

export interface IContentCompletionCallback {
    completionCallback: (element: HTMLElement, content: IContentAttributes) => void;
    contentType: string;
}

export interface IQueuedContent {
    contentElement: HTMLElement;
    succeeded: boolean;
}

/**
 * The IContentAttributes are the basic information that describes all content (css/javascript)
 * loaded into the page and managed by the content service.
 */
export interface IContentAttributes {
    /**
     * Length of the content associated with this file.
     */
    contentLength: number;

    /**
     * Unique id of a content no matter it is served from CDN or not.
     */
    clientId: string;

    /**
     * The contributionId this file content is being served from.
     */
    contributionId: string;

    /**
     * The type of content, either text/css or text/javascript.
     */
    contentType: string;

    /**
     * Integrity attribute to specify for the script
     */
    integrity?: string;

    /**
     * The namespace roots for modules contained in this content
     */
    moduleNamespaces?: string[];

    /**
     * Priority to load the content. Lower value is loaded first.
     */
    priority?: number;

    /**
     * Location of the content for download.
     */
    url: string;
}

/**
 * When a piece of content has completed its loading process it is represented by
 * ICompletedContent which describes the content attributes and the element that
 * contains the loaded content.
 */
export interface ICompletedContent {
    /**
     * Attributes that describe the content that has been completely loaded.
     */
    contentAttributes: IContentAttributes;

    /**
     * The HTMLElement that contains the loaded content.
     */
    contentElement: ContentLoaderHTMLElement;

    /**
     * Timestamp when the content element was injected into the DOM (JavaScript window.performance.now)
     */
    loadStart?: number;

    /**
     * Timestamp when the content finished loading (JavaScript window.performance.now)
     */
    loadEnd: number;

    /**
     * Timestamp when the resource that loaded previous to this one finished loading.
     */
    previousResourceLoadEnd?: number;
}

/**
 * Once all content associated with a contribution have been loaded the content
 * service will fire an event declaring the completion. This is fired even when
 * there were errors loading some files.
 */
export interface ICompletedContribution {
    /**
     * The contributionId that has completed.
     */
    contributionId: string;

    /**
     * True if the contribution has been resolved in the current/active page context.
     * False if this content was loaded by a previous page context but its contribution has
     * not yet been processed in the current page context.
     */
    resolved: boolean;
}

/**
 * The content service is designed to ensure scripts are available on the page.
 * This includes css and javascript. Scripts can be included one at a time or in
 * batch. Once all scripts have been downloded and compiled the promise returned
 * will be completed. If there is an error downloading or processing a script the
 * promise will be rejected.
 *
 * Events will fire for all content on the page, not just content that is added
 * after the subscriber is attached to the observable.
 *
 * NOTE: The contributionComplete event is only fired when the bulk loadScripts
 *  API is used. If the caller uses the loadScript method, only the content events
 *  will be fired.
 *
 * Subscribable events:
 *  action: contentComplete -> ICompletedContent
 *  action: contentLoadError -> ICompletedContent
 *  action: contributionComplete -> ICompletedContribution
 */
export interface IVssContentService
    extends IVssObservableService<ICompletedContent | ICompletedContribution, "contentComplete" | "contentLoadError" | "contributionComplete"> {
    /**
     * Get the loaded content lengths for the given contribution id
     *
     * @param contributionId Id of the contribution
     */
    getContributionContentLengths(contributionId: string): Readonly<{ [contentType: string]: number }> | undefined;

    /**
     * Determines whether the specified content (scripts and css) has been loaded (by its contribution id)
     *
     * @param contributionId - Contribution id of the content to check for
     *
     * @returns Boolean, true if contribution is loaded, false otherwise.
     */
    isLoaded(contributionId: string): boolean;

    /**
     * Method that will load one or more file contents css or javascript.
     *
     * @param contents - Defines the details of one or more contents that need to be loaded.
     *
     * @returns Promise that is completed when all content has finished loading into the DOM.
     */
    loadScripts(contents: IContentAttributes[]): Promise<IContentAttributes[]>;

    /**
     * Reset the resolved state of the content contributions that have already been loaded
     */
    resetResolvedContent(): void;
}

export const enum ContentType {
    javaScript = "text/javascript",
    css = "text/css"
}

class VssContentLoader {
    private pendingContent: { [contentLocation: string]: { startTime: number; loadPromise: Promise<IContentAttributes> } } = {};
    private loadedContent: { [contentLocation: string]: ICompletedContent } = {};
    private contentLengthsByContributionId: { [contributionId: string]: { [contentType: string]: number } } = {};

    private observable = new Observable<ICompletedContent>();

    constructor() {
        // Update the contentLoaded function with our service function.
        window.__contentLoaded = this.loadComplete.bind(this);
        window.__loadScript = this.loadScript.bind(this);
    }

    public isContentAvailable(content: IContentAttributes): boolean {
        return !!this.loadedContent[this.getContentId(content)];
    }

    public getContributionContentLengths(contributionId: string): Readonly<{ [contentType: string]: number }> | undefined {
        return this.contentLengthsByContributionId[contributionId];
    }

    public loadScript(content: IContentAttributes): Promise<IContentAttributes> {
        let loadPromise: Promise<IContentAttributes>;

        const contentId: string = this.getContentId(content);

        // If the content has already been loaded we will just return a resolved promise.
        if (this.loadedContent[contentId]) {
            loadPromise = Promise.resolve(content);
        }

        // Check if we have an outstanding promise for this pendingContent.
        else if (this.pendingContent[contentId]) {
            loadPromise = this.pendingContent[contentId].loadPromise;
        } else {
            const startTime = window.performance.now();
            loadPromise = new Promise<IContentAttributes>((resolve, reject) => {
                let contentElement: HTMLScriptElement | HTMLLinkElement;

                // Build the content element that should be placed in the head.
                if (content.contentType === ContentType.javaScript) {
                    contentElement = document.createElement("script");
                    contentElement.async = content.priority ? content.priority >= 100 : false;
                    contentElement.src = content.url;
                    if (content.integrity) {
                        contentElement.integrity = content.integrity;
                        contentElement.crossOrigin = "anonymous";
                    }
                    if (content.moduleNamespaces && content.moduleNamespaces.length) {
                        if (!window.__moduleNamespaces) {
                            window.__moduleNamespaces = [];
                        }
                        window.__moduleNamespaces = window.__moduleNamespaces.concat(content.moduleNamespaces);
                    }
                } else {
                    contentElement = document.createElement("link");
                    contentElement.href = content.url;
                    contentElement.rel = "stylesheet";
                    // not in TypeScript's lib.dom.d.ts as of 2.7, but should be in 2.8
                    ((contentElement as any).crossOrigin as string | null) = "anonymous";
                    if (content.integrity) {
                        contentElement.integrity = content.integrity;
                    }
                    contentElement.setAttribute("crossorigin", "anonymous");
                }

                contentElement.setAttribute("data-contentlength", content.contentLength.toString());

                if (content.clientId) {
                    contentElement.setAttribute("data-clientid", content.clientId);
                }

                if (content.contributionId) {
                    contentElement.setAttribute("data-sourcecontribution", content.contributionId);
                }

                // Add handlers for the completion of script loading.
                contentElement.addEventListener("load", (event: Event) => {
                    resolve(this.loadComplete(event.target as ContentLoaderHTMLElement, true));
                });
                contentElement.addEventListener("error", (event: Event) => {
                    reject(this.loadComplete(event.target as ContentLoaderHTMLElement, false));
                });

                // Insert the content element at the end of the head element.
                document.head.appendChild(contentElement);
            });

            this.pendingContent[contentId] = {
                startTime,
                loadPromise
            };
        }

        return loadPromise;
    }

    public subscribe(observer: (value: ICompletedContent, action: "contentComplete" | "contentLoadError") => void) {
        this.observable.subscribe(observer);
    }

    private getContentId(content: IContentAttributes): string {
        // If clientId exists use it along with content type for caching.
        // Otherwise use the content URL.
        return content.clientId || content.url;
    }

    private loadComplete(
        contentElement: ContentLoaderHTMLElement,
        succeeded: boolean,
        loadEnd?: number,
        previousResourceLoadEnd?: number
    ): IContentAttributes {
        // Determine the location of the content that was just loaded.
        const contentAttributes: IContentAttributes = {
            contentLength: parseInt(contentElement.getAttribute("data-contentlength") as string),
            clientId: contentElement.getAttribute("data-clientid") as string,
            contentType: contentElement.tagName.toUpperCase() === "SCRIPT" ? ContentType.javaScript : ContentType.css,
            contributionId: contentElement.getAttribute("data-sourcecontribution") as string,
            url: contentElement instanceof HTMLScriptElement ? contentElement.src : contentElement.href,
            integrity: contentElement.getAttribute("integrity") as string
        };

        const contentId = this.getContentId(contentAttributes);

        // Clear this from the pending content since it has completed.
        const existingContent = this.pendingContent[contentId];
        delete this.pendingContent[contentId];

        loadEnd = loadEnd || window.performance.now();
        const scriptData: ICompletedContent = {
            contentAttributes: contentAttributes,
            contentElement: contentElement,
            loadEnd: loadEnd
        };

        if (previousResourceLoadEnd) {
            scriptData.previousResourceLoadEnd = previousResourceLoadEnd;
        }

        if (existingContent) {
            scriptData.loadStart = existingContent.startTime;
        }

        if (succeeded) {
            // Save the element in the loaded content.
            this.loadedContent[contentId] = scriptData;

            // Record contribution content length
            if (contentAttributes.contributionId) {
                let contributionContentLength: { [contentType: string]: number } | undefined = this.contentLengthsByContributionId[
                    contentAttributes.contributionId
                ];
                if (!contributionContentLength) {
                    this.contentLengthsByContributionId[contentAttributes.contributionId] = contributionContentLength = {};
                }

                contributionContentLength[contentAttributes.contentType] =
                    (contributionContentLength[contentAttributes.contentType] || 0) + contentAttributes.contentLength;
            }

            this.observable.notify(scriptData, "contentComplete", true);
        } else {
            this.observable.notify(scriptData, "contentLoadError", true);
        }

        return contentAttributes;
    }
}

const contentLoader = new VssContentLoader();

class VssContentService
    extends VssObservableService<ICompletedContent | ICompletedContribution, "contentComplete" | "contentLoadError" | "contributionComplete">
    implements IVssContentService {
    private completedContributions: { [contributionId: string]: ICompletedContribution } = {};

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        // Bind to the loader's script load events
        contentLoader.subscribe((completedContent: ICompletedContent, action: "contentComplete" | "contentLoadError") => {
            this._notify(completedContent, action, true);
        });

        // Mark the initial contributions loaded through the server DOM elements as complete.
        const initialScripts = document.querySelectorAll("[data-sourcecontribution]");
        for (let scriptIndex = 0; scriptIndex < initialScripts.length; scriptIndex++) {
            const scriptElement = initialScripts[scriptIndex];
            const contributionId = scriptElement.getAttribute("data-sourcecontribution") as string;
            if (!this.completedContributions[contributionId]) {
                const completedContribution = { contributionId, resolved: true };
                this._notify(completedContribution, "contributionComplete", true);
                this.completedContributions[contributionId] = completedContribution;
            }
        }
    }

    public isLoaded(contributionId: string): boolean {
        return !!this.completedContributions[contributionId];
    }

    public loadScripts(contents: IContentAttributes[]): Promise<IContentAttributes[]> {
        const contentPromises: Promise<IContentAttributes>[] = [];
        const resources: { [contributionId: string]: number } = {};

        // Load each script tracking the promise for each file.
        for (const content of contents) {
            // If the contents are already available, we will not track it for contributionComplete.
            if (!contentLoader.isContentAvailable(content)) {
                // Track the number of scripts requested for each contributionId.
                const contributionId = content.contributionId;

                if (contributionId) {
                    if (!resources[contributionId]) {
                        resources[contributionId] = 1;
                    } else {
                        resources[contributionId] = resources[contributionId] + 1;
                    }
                }
            }

            // Shim the returned promise and track the completion state of the contribution.
            contentPromises.push(
                contentLoader.loadScript(content).then(
                    (attributes: IContentAttributes) => this.scriptComplete(attributes, resources),
                    (attributes: IContentAttributes) => this.scriptComplete(attributes, resources)
                )
            );
        }

        return Promise.all(contentPromises);
    }

    public getContributionContentLengths(contributionId: string): Readonly<{ [contentType: string]: number }> | undefined {
        return contentLoader.getContributionContentLengths(contributionId);
    }

    public resetResolvedContent(): void {
        for (const contributionId in this.completedContributions) {
            this.completedContributions[contributionId].resolved = false;
        }
    }

    private scriptComplete = (attributes: IContentAttributes, resources: { [contributionId: string]: number }): IContentAttributes => {
        // Determine if this completion is the last pending resource for this contribution
        const contributionId = attributes.contributionId;
        const resourceCount = resources[contributionId];
        if (resourceCount) {
            resources[contributionId] = resourceCount - 1;
            if (resourceCount === 1) {
                if (!this.completedContributions[contributionId]) {
                    const completedContribution = { contributionId, resolved: true };
                    this._notify(completedContribution, "contributionComplete", true);
                    this.completedContributions[contributionId] = completedContribution;
                } else {
                    this.completedContributions[contributionId].resolved = true;
                }
            }
        }

        return attributes;
    };
}

Services.add("IVssContentService", { serviceFactory: VssContentService, options: ServiceOptions.Persistant | ServiceOptions.Startup });
